import { type ReactElement } from 'react';
import { FileText } from 'lucide-react';

import { EmptyState } from '@/ui/EmptyState';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { PageHeader } from '@/ui/PageHeader';
import { Card, CardContent } from '@/ui/base/card';

import { useMeusTermos } from './dados';
import { TermoCard } from './TermoCard';

/**
 * `/credenciado/contratos` — os Termos de Adesão do credenciado, um cartão
 * por termo, com as Ordens de Serviço de cada um logo abaixo (decisão D-A da
 * Fase 3: a OS fica no termo, sem item de menu próprio).
 *
 * Sem `@ExigeTermoAtivo` de propósito: é aqui que o termo é assinado, antes de
 * estar ativo. As OS só são consultadas quando há termo ativo
 * (`ordens-servico/dados.ts`).
 *
 * Do protótipo não vêm os três cartões de resumo, as abas Todos, Vigentes e
 * Encerrados nem a busca por SEI: "Meus termos" não devolve o número do SEI.
 */
export function ContratosPage(): ReactElement {
  const consulta = useMeusTermos();
  const termos = consulta.data ?? [];

  return (
    <div className="min-w-0 space-y-4">
      <PageHeader
        title="Contratos e Termos"
        description="O seu Termo de Adesão ao Transporte Escolar Gratuito e as Ordens de Serviço dele: confira, assine e acompanhe."
      />
      <EstadoDaTela
        carregando={consulta.isPending}
        erro={consulta.error ?? null}
        aoTentarDeNovo={() => void consulta.refetch()}
        descricao="os seus termos"
        esqueleto="lista-de-cartoes"
      >
        {termos.length === 0 ? (
          <Card>
            <CardContent className="p-0">
              <EmptyState
                icon={FileText}
                title="Nenhum Termo de Adesão ainda"
                description="Depois da habilitação, o DTP elabora o seu Termo de Adesão e ele aparece aqui para assinatura."
              />
            </CardContent>
          </Card>
        ) : (
          <ul aria-label="Seus Termos de Adesão" className="space-y-4">
            {termos.map((termo) => (
              <li key={termo.id}>
                <TermoCard termo={termo} />
              </li>
            ))}
          </ul>
        )}
      </EstadoDaTela>
    </div>
  );
}

export default ContratosPage;
